import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import type {
  AdminPuzzleDto,
  AdminPuzzleListQuery,
  AdminPuzzleStatus,
  PuzzleGoal,
  PuzzleSourceType
} from '@chessx/shared/types'
import { PUZZLE_GOALS, PUZZLE_SOURCES } from '@chessx/shared/constants'
import { getPuzzleTitle } from '@chessx/shared/puzzle'

import FenBoard from './FenBoard'
import PuzzleBulkUpload from './PuzzleBulkUpload'
import {
  NEW_PUZZLE_ID,
  PUZZLES_PATH,
  errorMessage,
  fetchAllPuzzles,
  useDeletePuzzle,
  usePuzzleList
} from './PuzzlesApi'
import { downloadJson, toPuzzleInput } from './puzzleJson'
import { toastErrorMixin } from '../../utils/interact'
import './Puzzles.scss'

const PAGE_SIZE = 25

const STATUS_OPTIONS: AdminPuzzleStatus[] = ['all', 'active', 'inactive']

const Puzzles = () => {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [source, setSource] = useState<PuzzleSourceType | ''>('')
  const [goal, setGoal] = useState<PuzzleGoal | ''>('')
  const [status, setStatus] = useState<AdminPuzzleStatus>('all')
  const [page, setPage] = useState(1)
  const [pendingDelete, setPendingDelete] = useState<AdminPuzzleDto | null>(null)
  const [showBulk, setShowBulk] = useState(false)
  const [exporting, setExporting] = useState(false)

  const filters: AdminPuzzleListQuery = {
    search: search || undefined,
    source: source || undefined,
    goal: goal || undefined,
    status
  }
  const list = usePuzzleList({ ...filters, page, limit: PAGE_SIZE })
  const deletePuzzle = useDeletePuzzle()

  const puzzles = list.data?.puzzles ?? []
  const total = list.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  useEffect(() => {
    if (!list.isFetching && page > lastPage) setPage(lastPage)
  }, [lastPage, page, list.isFetching])

  const resetToFirstPage =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value)
      setPage(1)
    }

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSearch(searchInput.trim())
    setPage(1)
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    await deletePuzzle.mutateAsync(pendingDelete.id).catch(() => undefined)
    setPendingDelete(null)
  }

  const exportFiltered = async () => {
    setExporting(true)
    try {
      const all = await fetchAllPuzzles(filters)
      downloadJson(`puzzles-${new Date().toISOString().slice(0, 10)}.json`, all.map(toPuzzleInput))
    } catch (error) {
      toastErrorMixin.fire({ title: errorMessage(error) })
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="pz-page">
      <div className="pz-header">
        <div>
          <h3 className="pz-title">Puzzles</h3>
          <span className="pz-subtitle">
            Browse, create, edit and remove puzzles served to the app
          </span>
        </div>
        <div className="pz-header-actions">
          <button className="pz-btn pz-btn-ghost" disabled={exporting} onClick={exportFiltered}>
            {exporting ? 'Exporting…' : 'Export JSON'}
          </button>
          <button className="pz-btn pz-btn-ghost" onClick={() => setShowBulk(true)}>
            Bulk upload
          </button>
          <button
            className="pz-btn pz-btn-primary"
            onClick={() => navigate(`${PUZZLES_PATH}/${NEW_PUZZLE_ID}`)}
          >
            New puzzle
          </button>
        </div>
      </div>

      <form className="pz-filters" onSubmit={submitSearch}>
        <input
          className="pz-input pz-search"
          placeholder="Puzzle key, theme or id"
          value={searchInput}
          onChange={event => setSearchInput(event.target.value)}
        />
        <select
          className="pz-input"
          value={source}
          onChange={event => resetToFirstPage(setSource)(event.target.value as PuzzleSourceType)}
        >
          <option value="">All sources</option>
          {PUZZLE_SOURCES.map(option => (
            <option key={option}>{option}</option>
          ))}
        </select>
        <select
          className="pz-input"
          value={goal}
          onChange={event => resetToFirstPage(setGoal)(event.target.value as PuzzleGoal)}
        >
          <option value="">All goals</option>
          {PUZZLE_GOALS.map(option => (
            <option key={option}>{option}</option>
          ))}
        </select>
        <select
          className="pz-input"
          value={status}
          onChange={event => resetToFirstPage(setStatus)(event.target.value as AdminPuzzleStatus)}
        >
          {STATUS_OPTIONS.map(option => (
            <option key={option}>{option}</option>
          ))}
        </select>
        <button className="pz-btn pz-btn-search" type="submit">
          Search
        </button>
      </form>

      <div className="pz-card">
        {list.isLoading ? (
          <div className="pz-empty">Loading puzzles…</div>
        ) : list.isError ? (
          <div className="pz-empty pz-error-text">{errorMessage(list.error)}</div>
        ) : puzzles.length ? (
          <div className="pz-table-wrap">
            <table className="pz-table">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Puzzle</th>
                  <th>To move</th>
                  <th>Rating</th>
                  <th>Goal</th>
                  <th>Gems</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {puzzles.map(puzzle => (
                  <tr key={puzzle.id}>
                    <td
                      className="pz-col-board"
                      onClick={() => navigate(`${PUZZLES_PATH}/${puzzle.id}`)}
                    >
                      <FenBoard
                        fen={puzzle.root.fen}
                        flipped={puzzle.sideToMove === 'black'}
                        size={96}
                      />
                    </td>
                    <td className="pz-col-key">
                      <span className="pz-puzzle-title">{getPuzzleTitle(puzzle)}</span>
                      <code>{puzzle.puzzleKey}</code>
                      <span className="pz-meta">
                        {puzzle.ownerUserId && (
                          <span className="pz-badge" title={puzzle.ownerUserId}>
                            user-owned
                          </span>
                        )}
                      </span>
                      <span className="pz-themes">{puzzle.themes.join(' · ')}</span>
                    </td>
                    <td>
                      <span className={`pz-side side-${puzzle.sideToMove}`}>
                        {puzzle.sideToMove === 'white' ? 'White' : 'Black'}
                      </span>
                    </td>
                    <td>{puzzle.rating}</td>
                    <td>
                      {puzzle.goal}
                      {puzzle.mateIn ? ` in ${puzzle.mateIn}` : ''}
                    </td>
                    <td className="pz-col-gems">{puzzle.gemReward ?? '—'}</td>
                    <td>{puzzle.order}</td>
                    <td>
                      <span
                        className={`pz-badge ${puzzle.isActive ? 'pz-badge-ok' : 'pz-badge-off'}`}
                      >
                        {puzzle.isActive ? 'active' : 'inactive'}
                      </span>
                    </td>
                    <td className="pz-col-actions">
                      <button
                        className="pz-btn pz-btn-ghost"
                        onClick={() => navigate(`${PUZZLES_PATH}/${puzzle.id}`)}
                      >
                        Edit
                      </button>
                      <button
                        className="pz-btn pz-btn-danger"
                        onClick={() => setPendingDelete(puzzle)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="pz-empty">No puzzles match these filters.</div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="pz-pager">
          <button
            className="pz-btn pz-btn-ghost"
            disabled={page <= 1}
            onClick={() => setPage(current => current - 1)}
          >
            Previous
          </button>
          <span className="pz-pager-label">
            Page {page} of {lastPage} · {total} puzzles
          </span>
          <button
            className="pz-btn pz-btn-ghost"
            disabled={page >= lastPage}
            onClick={() => setPage(current => current + 1)}
          >
            Next
          </button>
        </div>
      )}

      <PuzzleBulkUpload show={showBulk} onHide={() => setShowBulk(false)} />

      <Modal centered show={Boolean(pendingDelete)} onHide={() => setPendingDelete(null)}>
        <div className="pz-modal">
          <h4 className="pz-modal-title">Delete puzzle</h4>
          <p className="pz-modal-body">
            Permanently delete <code>{pendingDelete?.puzzleKey}</code>? Players will stop seeing it
            on their next sync. To hide it without losing it, edit it and untick Active instead.
          </p>
          <div className="pz-modal-actions">
            <button className="pz-btn pz-btn-ghost" onClick={() => setPendingDelete(null)}>
              Cancel
            </button>
            <button
              className="pz-btn pz-btn-danger"
              disabled={deletePuzzle.isPending}
              onClick={confirmDelete}
            >
              {deletePuzzle.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Puzzles
